import React, { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { dataCentralManager } from '@/core/DataCentralManager';
import { useQueryClient } from '@tanstack/react-query';
import { profileCacheService } from '@/services/ProfileCacheService';
import { debugLogger } from '@/utils/debugLogger';
import { LogOut } from 'lucide-react';

const Logout: React.FC = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  useEffect(() => {
    if (isProcessing) return;

    const performLogout = async () => {
      setIsProcessing(true);
      
      try {
        debugLogger.debug('Logout: Starting fast logout process');
        
        // Execute all cleanup operations without delays
        profileCacheService.clearProfileData();
        dataCentralManager.cleanup();
        queryClient.clear();
        
        // Clear localStorage (keep essentials)
        const itemsToKeep = ['theme', 'language'];
        const storage = { ...localStorage };
        localStorage.clear();
        itemsToKeep.forEach(key => {
          if (storage[key]) {
            localStorage.setItem(key, storage[key]);
          }
        });
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Execute Supabase signOut
        debugLogger.debug('Logout: Executing Supabase signOut');
        await signOut();
        
        // Minimal delay for visual feedback (500ms)
        setTimeout(() => {
          debugLogger.debug('Logout: Redirecting to auth');
          navigate('/auth', { replace: true });
        }, 500);
        
      } catch (error) {
        debugLogger.error('Logout: Error during logout:', error);
        
        // Immediate redirect on error
        navigate('/auth', { replace: true });
      }
    };

    performLogout();
  }, [signOut, navigate, queryClient, isProcessing]);

  return (
    <div className="w-full h-screen flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="text-center max-w-md mx-auto p-8 animate-fade-in">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <LogOut className="h-12 w-12 text-luxury-pinkBright animate-pulse" />
        </div>

        {/* Status Text */}
        <h2 className="text-xl font-semibold text-white mb-4">
          Saindo...
        </h2>
        
        {/* Spinner */}
        <div className="w-8 h-8 border-2 border-white/20 border-t-luxury-pinkBright rounded-full animate-spin mx-auto" />
      </div>
    </div>
  );
};

export default Logout;
